import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getDealDetail } from '../services/apiService';
import { dealAvailability } from '../services/dealAvailability';
import './DealPage.css';

function fullDutchDate(date) {
  return date.toLocaleDateString('nl-NL', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function getCountdown(expiresAtIso) {
  if (!expiresAtIso) return null;
  const end = new Date(expiresAtIso).getTime();
  const now = Date.now();
  if (end <= now) return { text: 'Verlopen', expired: true, urgent: false };
  const diffMin = Math.floor((end - now) / 60000);
  const hours = Math.floor(diffMin / 60);
  const mins  = diffMin % 60;
  const urgent = diffMin <= 120;
  if (urgent) {
    return {
      text: hours > 0 ? `Verloopt over ${hours}u ${mins}m!` : `Verloopt over ${mins}m!`,
      expired: false,
      urgent: true,
    };
  }
  if (hours >= 48) {
    return { text: `Nog ${Math.floor(hours / 24)} dagen beschikbaar`, expired: false, urgent: false };
  }
  return {
    text: hours > 0 ? `Nog ${hours}u ${mins}m beschikbaar` : `Nog ${mins}m beschikbaar`,
    expired: false,
    urgent: false,
  };
}


function DealLocationSection({ shopData, dealName }) {
  const [showInstructions, setShowInstructions] = useState(false);

  function openNavigation(lat, lng, name) {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS) {
      window.location.href =
        `maps://maps.apple.com/?daddr=${lat},${lng}&q=${encodeURIComponent(name)}`;
    } else {
      window.location.href =
        `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    }
  }

  return (
    <div className="deal__location-section">
      {shopData?.lat && shopData?.lng && (
        <button
          className="deal__nav-btn"
          onClick={() => openNavigation(shopData.lat, shopData.lng, dealName)}
        >
          Navigeer naar {dealName}
        </button>
      )}
      {shopData?.instructions && (
        <>
          <button
            className="deal__instructions-btn"
            onClick={() => setShowInstructions(!showInstructions)}
          >
            <span>Hoe vind ik deze winkel?</span>
            <svg
              className={`deal__instructions-caret${showInstructions ? ' deal__instructions-caret--open' : ''}`}
              width="14" height="14" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.5"
              strokeLinecap="round" strokeLinejoin="round"
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
          {showInstructions && (
            <div className="deal__instructions-panel">
              <div className="deal__instructions-header">
                <strong>Looproute naar {dealName}</strong>
              </div>
              {shopData.instructions.includes('·') ? (
                <div className="deal__instructions-steps">
                  {shopData.instructions
                    .split('·')
                    .filter((s) => s.trim())
                    .map((step, i) => (
                      <div key={i} className="deal__instructions-step">
                        <div className="step-number">{i + 1}</div>
                        <div className="step-text">{step.trim()}</div>
                      </div>
                    ))}
                </div>
              ) : (
                <p className="deal__instructions-text">{shopData.instructions}</p>
              )}
              {shopData?.lat && shopData?.lng && (
                <button
                  className="deal__nav-btn-small"
                  onClick={() => openNavigation(shopData.lat, shopData.lng, dealName)}
                >
                  Navigeer naar ingang
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function availabilityLabel(a) {
  switch (a.reason) {
    case 'paused': return 'Gepauzeerd';
    case 'ended': return 'Beëindigd';
    case 'expired': return 'Verlopen';
    case 'scheduled': return 'Binnenkort';
    case 'shop-closed': return 'Winkel gesloten';
    case 'outside-hours': return 'Buiten geldigheidstijd';
    default: return 'Niet actief';
  }
}

export default function DealPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState(null);
  const [shareToast, setShareToast] = useState('');

  // Fetch the deal from the backend. There is no local/demo fallback: an
  // unknown id shows the "niet gevonden" state rather than placeholder data.
  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const d = await getDealDetail(id);
        if (!cancelled) { setDeal(d); setLoading(false); }
      } catch {
        if (!cancelled) { setDeal(null); setLoading(false); }
      }
    }

    load();
    return () => { cancelled = true; };
  }, [id]);

  // The backend deal carries the retailer's coordinates + looproute
  // instructions directly (retailerLat/Lng/Instructions, normalised to
  // lat/lng/instructions), so the location section reads them off the deal.
  const shopLocationData = useMemo(() => {
    if (!deal) return null;
    const { lat, lng, instructions } = deal;
    if (lat || lng || instructions) return { lat, lng, instructions };
    return null;
  }, [deal]);

  const handleShare = async () => {
    if (!deal) return;
    // The page's own origin, so a shared link points at wherever this build is
    // hosted (prototype.joinwalkin.com now, the real app later).
    const dealUrl = `${window.location.origin}/deal-preview/${deal.id}`;
    const shareData = {
      title: deal.title,
      text: `${deal.shopName}${deal.area ? ` in ${deal.area}` : ''} heeft een WalkIn deal voor je: "${deal.title}". Open de WalkIn app om hem in te leveren!`,
      url: dealUrl,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(dealUrl);
        setShareToast('Link gekopieerd!');
        setTimeout(() => setShareToast(''), 2000);
      }

    } catch {
      /* user cancelled share — no-op */
    }
  };

  useEffect(() => {
    if (!deal?.expiresAt) { setCountdown(null); return; }
    setCountdown(getCountdown(deal.expiresAt));
    const interval = setInterval(
      () => setCountdown(getCountdown(deal.expiresAt)),
      60000
    );
    return () => clearInterval(interval);
  }, [deal?.expiresAt]);

  if (loading) {
    return (
      <div className="deal-404">
        <p>Deal laden…</p>
      </div>
    );
  }

  if (!deal) {
    return (
      <div className="deal-404">
        <p>Deal niet gevonden.</p>
        <button onClick={() => navigate('/feed')}>Terug naar feed</button>
      </div>
    );
  }

  const expiryDate = deal.expiresAt ? new Date(deal.expiresAt) : new Date();
  const availability = dealAvailability(deal);
  // Paused / ended / expired deals cannot be claimed at all. Outside the
  // shop's hours the QR can still be shown — the walker may be on the way.
  const blocked = ['ended', 'paused', 'expired', 'missing', 'scheduled'].includes(availability.reason);

  return (
    <div className="deal">
      <header className="deal__header">
        <button className="deal__back" onClick={() => navigate(-1)} aria-label="Terug">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 5l-7 7 7 7" />
          </svg>
        </button>
        <span className="deal__header-title">Deal Details</span>
        <button className="deal__share" aria-label="Delen" onClick={handleShare}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
          </svg>
        </button>
      </header>

      {shareToast && <div className="deal__toast">{shareToast}</div>}

      <div className="deal__hero">
        <div className="deal__limited-badge">Limited deal</div>
      </div>

      <div className="deal__card">
        <div className="deal__shop-row">
          <div className="deal__shop-info">
            <span className="deal__shop">{deal.shopName}</span>
            <span className="deal__distance-text">{deal.area}</span>
          </div>
        </div>

        <h1 className="deal__title">{deal.title}</h1>
        {deal.description && (
          <p className="deal__description">{deal.description}</p>
        )}

        <div className="deal__tags">
          {deal.category && (
            <span className="deal__tag deal__tag--category">
              {deal.category}
            </span>
          )}
          {availability.live ? (
            <span className="deal__tag deal__tag--today">Nu actief</span>
          ) : (
            <span className="deal__tag deal__tag--inactive">{availabilityLabel(availability)}</span>
          )}
        </div>

        <div className={`deal__validity${countdown?.expired ? ' deal__validity--expired' : ''}`}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
            stroke={countdown?.expired ? 'rgba(250,248,245,0.2)' : '#4A9EE8'}
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            style={{ flexShrink: 0 }}>
            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
          </svg>
          <div className="deal__validity-text">
            <div className="deal__validity-until">
              {deal.validFrom && (() => {
                const vf = new Date(deal.validFrom);
                const ve = expiryDate;
                const sameDay =
                  vf.toDateString() === ve.toDateString();
                const time = `${String(vf.getHours()).padStart(2,'0')}:${String(vf.getMinutes()).padStart(2,'0')}`;
                if (sameDay) {
                  return `Geldig van ${time} tot 23:59 op ${fullDutchDate(ve)}`;
                }
                return `Geldig van ${fullDutchDate(vf)} ${time} tot ${fullDutchDate(ve)} 23:59`;
              })()}
              {!deal.validFrom && `Geldig tot 23:59 op ${fullDutchDate(expiryDate)}`}
            </div>
            <div
              className={
                'deal__validity-countdown' +
                (countdown?.urgent ? ' deal__validity-countdown--urgent' : '')
              }
            >
              {countdown ? countdown.text : '—'}
            </div>
          </div>
        </div>

        {(shopLocationData?.instructions ||
          (shopLocationData?.lat && shopLocationData?.lng)) && (
          <DealLocationSection
            shopData={shopLocationData}
            dealName={deal.shopName}
          />
        )}

        <div className="deal__details-grid">
          <div className="deal__detail-cell">
            <div className="deal__detail-label">Winkel</div>
            <div className="deal__detail-value">{deal.shopName}</div>
          </div>
          <div className="deal__detail-cell">
            <div className="deal__detail-label">Categorie</div>
            <div className="deal__detail-value">{deal.category || '—'}</div>
          </div>
          <div className="deal__detail-cell">
            <div className="deal__detail-label">Gebied</div>
            <div className="deal__detail-value">{deal.area || '—'}</div>
          </div>
          {/* The gebiedscode is deliberately not shown to walkers; it stays in
              the database and on the retailer/admin screens. */}
          <div className="deal__detail-cell">
            <div className="deal__detail-label">Adres</div>
            <div className="deal__detail-value">{deal.retailerAddress || '—'}</div>
          </div>
        </div>

        <div style={{ height: 88 }} />
      </div>

      <div className="deal__cta-wrap">
        <button
          className="deal__cta"
          onClick={() => navigate(`/qr/${deal.id}`)}
          disabled={countdown?.expired || blocked}
        >
          {blocked ? 'Niet beschikbaar' : 'Toon QR code →'}
        </button>
      </div>
    </div>
  );
}
